import * as React from 'react';
import {
  PropsWithChildren, useEffect, useState,
} from 'react';
import {
  Button,
  Confirm,
  Datagrid,
  Form,
  Identifier,
  required,
  useCreate,
  useExpanded,
  useGetIdentity,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useSaveContext,
  useTranslate,
  useUnselectAll,
  maxLength,
} from 'react-admin';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TableHead,
  TextField as MuiTextField,
  Tooltip,
  useTheme,
  Stack,
  InputLabel,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Edit } from '@react-admin/ra-rbac';
import { MapContainer, ScaleControl } from 'react-leaflet';
import L, { LatLngBoundsExpression, LatLngExpression, Map } from 'leaflet';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TuneIcon from '@mui/icons-material/Tune';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  AnyGeometry,
  AnyLoraConfig,
  Asset,
  Customer,
  Device,
  DeviceType,
  LoraDeviceTypes,
  MilesightConfig,
  PointGeometry,
} from '@x-guard/xgac-types/xgac';
import PersonIcon from '@mui/icons-material/Person';
import CircularProgress from '@mui/material/CircularProgress';
import { useGetList, useUpdate } from 'ra-core';
import { useFormContext, useFormState, FieldValues } from 'react-hook-form';
import _ from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RoomIcon from '@mui/icons-material/Room';
import IosShareIcon from '@mui/icons-material/IosShare';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import CancelIcon from '@mui/icons-material/Cancel';
import WifiTetheringErrorIcon from '@mui/icons-material/WifiTetheringError';
import SignalCellularConnectedNoInternet1BarIcon from '@mui/icons-material/SignalCellularConnectedNoInternet1Bar';
import { StyledButton } from '../buttons/StyledButton';
import authProvider from '../../../utils/authProvider';
import { atsDataProvider } from '../../../dataProviders/atsDataProvider';
import { httpClient, typedHttpClient } from '../../../utils/httpClient';
import { CURRENT_CUSTOMER_CHANGED_EVENT, getCurrentCustomer } from '../../../lib/currentCustomer';
import { XGAC_MAIN_API_URL } from '../../../config';
import { wait } from '../../../lib/websockets/webSocketHelpers';
import { GoogleCoordinatesInput } from '../components/inputs/GoogleCoordinatesInput';
import { xgacDataProvider } from '../../../dataProviders/xgacDataProvider';
import { TestButtonButton } from '../components/TestButtonButton';
import { AddBuildingDialog } from '../components/AddBuildingDialog';
import bhvIcon from '../svg/bhv-button.svg';
import gatewayIcon from '../svg/gateway_icon.svg';
import buildingIcon from '../svg/building.svg';
import arrowDownIcon from '../svg/arrow_down.svg';

import {
  CenterControl, CustomLayerControl, FloorFilter, ListFilter,
} from '../components/LeafletControls';
import { buttonAssetExporter } from '../../../utils/customExporter';
import { AddButtonOrGatewayDialog } from '../components/AddButtonOrGatewayDialog';
import {
  CustomerLink, DeviceConnectionStatus, DeviceFilter, DeviceStatus,
} from '../../../lib/constants/customTypes';
import { DeviceFilters } from '../components/deviceFilters';
import { ButtonPageButtons, ButtonWithIconAndLabel } from '../buttons/ButtonPageButtons';
import { useOverlay } from '../../../utils/useOverlay';
import { ButtonMapMarker } from '../map/ButtonMapMarker';
import { GrayTextInputWithUpperLabel } from '../components/inputs/GrayTextInputWithUpperLabel';
import { GrayGoogleCoordinatesInputWithUpperLabel } from '../components/inputs/GrayGoogleCoordinatesInputWithUpperLabel';
import { BhvkDialogCancelButton, BhvkDialogConfirmButton, BhvkDialogSaveButton } from '../buttons/ActionButtons';
import { buildLabelPrintUrl } from './labelPrintUrl';
import { GrayRaIntegerInputWithUpperLabel } from '../components/inputs/GrayRaIntegerInputWithUpperLabel';

const apiUrl = XGAC_MAIN_API_URL;
const api = typedHttpClient();
const Grid = Grid2;

const alarmCodeToColorMapping = {
  ALARM_RED: 'red',
  ALARM_ORANGE: 'orange',
  ALARM_GREEN: 'green',
};

export const EmptyHeader = () => {

  return (
    <TableHead></TableHead>
  );

};

const updateMonitoredZones = async () => {

  const currentCustomer = getCurrentCustomer()?.value;
  if (currentCustomer) {

    await api.specials.bhvKnopControllerUpdateMonitoredZones({
      customer: {
        _id: currentCustomer,
        _ref: 'Customer',
      },
    });

  }

};

const getDeviceStatus = (record: Asset & { device: Device }): DeviceConnectionStatus => {

  const isTrial = authProvider.getRoles().includes('bhvk_trial');
  const currentTimestamp = new Date().getTime();
  const lastSeenTimestamp = new Date(record.lastObservationAt || 0).getTime();

  if (isTrial === true) {

    return 'connected';

  }

  if (record.device.type === 'loraGateway') {

    const lastObservation = new Date(record.lastObservationAt || 0).getTime();

    return (currentTimestamp - lastObservation) < 1000 * 60 * 30 ? 'connected' : 'disconnected';

  }

  if (record.device.battery && record.device.battery <= 20) {

    return 'empty';

  }

  if ((currentTimestamp - lastSeenTimestamp) < 1000 * 60 * 60 * 40) {

    return 'connected';

  }

  return 'disconnected';

};
const DeviceEditFields = (props: { onCollapse: () => void; setConfirmDialogOpen: (open: boolean) => void }) => {

  const record = useRecordContext();
  const { getValues, reset } = useFormContext();
  const translate = useTranslate();
  const notify = useNotify();
  const { save } = useSaveContext();
  const { isDirty, isSubmitting } = useFormState();

  if (!record) {

    return null;

  }

  const handleSave = async (newData: Partial<Asset & { device: Device }>) => {

    const data = {
      ...getValues(),
      ...newData,
    } as Asset & { device: Device };

    if (_.isEqual(record.position?.coordinates, data.position?.coordinates)) {

      await save?.(data);
      return;

    }
    props.setConfirmDialogOpen(true);

  };

  const handleCancel = () => {

    reset(record);
    props.onCollapse();

  };

  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      <GrayTextInputWithUpperLabel
        source="name"
        label="general.fields.name"
        isRequired
        helperText={false}
        fullWidth
        size="small"
        sx={{ mt: 0, mb: 0 }}
        validate={[required(), maxLength(99, 'ra.notification.name_too_long')]}
        onBlur={(e) => {

          const name = e.target.value ?? '';
          if (name.length > 0 && name.length < 100) {

            handleSave({ name });

          }
          if (name.length >= 100) {

            notify('ra.notification.name_too_long', { type: 'warning' });

          }

        }}
        onKeyDown={(e) => {

          if (e.key === 'Enter') {

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            e.target.blur();
            e.preventDefault();

          }

        }}
      />
      <GrayGoogleCoordinatesInputWithUpperLabel
        source="position"
        label="general.text.location"
        placeholder={translate('resources.buttons.text.building_dialog.0.input_label')}
        sx={{ mt: 0, mb: 0 }}
        country={['nl', 'be', 'de']}
        helperText={false}
        size="small"
        multiline
        handleSave={handleSave}
      />
      <GrayRaIntegerInputWithUpperLabel
        source="position.properties.address.floor"
        label="general.text.map_floors"
        needsSaving={true}
      />
      <Stack
        direction="row"
        justifyContent="end"
        spacing={1}
        sx={{ mt: 2, pt: 1 }}
      >
        <BhvkDialogCancelButton onClick={handleCancel} size="small" onMouseDown={(e) => e.preventDefault()}>
          {translate('ra.action.cancel')}
        </BhvkDialogCancelButton>
        <BhvkDialogSaveButton type="submit" size="small" disabled={!isDirty || isSubmitting} onMouseDown={(e) => e.preventDefault()}>
          {translate('ra.action.save')}
        </BhvkDialogSaveButton>
      </Stack>
    </Stack>
  );

};

const DeviceStatusWarning = (props: { active: DeviceConnectionStatus }) => {

  const { active } = props;
  const translate = useTranslate();
  const redirect = useRedirect();

  if (active === 'connected') {

    return null;

  }

  if (active === 'disconnected' || active === 'empty') {

    return <Box>
      <StyledButton
        style={{ width: '100%', marginBottom: '15px' }}
        color={'warning'}
        label="general.text.solve_issues"
        onClick={() => {

          redirect('/help/hardware/er-is-een-knop-offline-wat-moet-ik-doen');

        }}
      />
    </Box>;

  }

  if (active === 'highNoise' || active === 'lowRSSI') {

    const urlMap = {
      highNoise: '/help/hardware/het-radiosignaal-bevat-ruis/',
      lowRSSI: '/help/hardware/de-knop-staat-te-ver-weg-van-het-basisstation',
    };

    return <Alert
      style={{ marginTop: '1em', marginBottom: '1.5em' }}
      severity="warning"
      action={
        <StyledButton
          style={{ width: '100%', marginBottom: '15px' }}
          color={'warning'}
          label="general.text.solve_issues"
          onClick={() => {

            redirect(urlMap[active]);

          }}
        />
      }>
      <AlertTitle>{translate(`resources.buttons.text.status.${active}`)}</AlertTitle>
    </Alert>;

  }

  return null;

};

const EditFormWithSubmit = (props: {
  children: React.ReactNode;
  setConfirmDialogOpen: (open: boolean) => void;
  confirmDialogOpen: boolean;
  onCollapse: () => void;
}) => {

  const formRecord = useRecordContext<Asset & { device: Device }>();
  const { save } = useSaveContext();

  const onSubmit = async (values: FieldValues) => {

    const data = values as Asset & { device: Device };

    if (_.isEqual(formRecord?.position?.coordinates, data.position?.coordinates)) {

      await save?.(data);
      return;

    }

    props.setConfirmDialogOpen(true);

  };

  return (
    <Form onSubmit={onSubmit}>
      {props.children}
    </Form>
  );

};

const DeviceEditForm = (props: {
  setNeedsMonitoredZonesUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  onCollapse: () => void;
}) => {

  const record: Asset & { device: Device } | undefined = useRecordContext();
  const translate = useTranslate();
  const notify = useNotify();
  const roles = authProvider.getRoles();
  const refresh = useRefresh();
  const [active, setActive] = useState<DeviceConnectionStatus>('unknown');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const isTrial = roles.includes('bhvk_trial');
  useEffect(() => {

    if (!record) {

      return;

    }
    if (record?.device.deviceId?.substring(0, 6) === 'Unpaid') {

      setActive('connected');
      return;

    }

    setActive(getDeviceStatus(record));

  }, [isTrial, record]);
  const onDeviceSuccess = () => {

    notify('ra.notification.updated', { messageArgs: { smart_count: 1 }, type: 'info' });
    props.setNeedsMonitoredZonesUpdate(true);
    refresh();

  };

  const transform = (data: any) => {

    delete data.device;
    delete data.state;
    return {
      ...data,
      position: {
        ...data.position,
        coordinates: [data.position.coordinates[0], data.position.coordinates[1]],
        properties: {
          ...data.position.properties,
          provider: 'static',
          address: {
            ...data.position.properties.address,
            formattedAddress: data.position.properties.address.formattedAddress,
          },
        },
      },
    };

  };

  const ExternalIdField = () => {

    if (!record) {

      return null;

    }
    const externalId = record.externalId?.split('-')?.slice(-1)[0];
    const type = record.device?.type;

    return (
      <h4 style={{ marginBottom: '10px', marginTop: '0' }}>
        {translate(type === 'lora' ? 'general.text.button' : 'general.text.gateway')}{externalId ? ` ${externalId}` : translate('general.text.number_unknown') }
        {externalId
            && (<Tooltip
              title={translate(type === 'lora' ? 'resources.buttons.text.button_number_tooltip' : 'resources.buttons.text.gateway_number_tooltip')}
              arrow
            >
              <HelpOutlineIcon
                style={{
                  verticalAlign: 'middle',
                  position: 'relative',
                  top: '-1px',
                  marginLeft: '5px',
                }} />
            </Tooltip>)}
      </h4>
    );

  };

  const TestButtonOrOrderExpl = () => {

    if (!record) {

      return null;

    }
    if (record.device.deviceId?.substring(0, 6) === 'Unpaid') {

      return (
        <Chip variant="outlined" label={translate('resources.buttons.text.not_ordered')}/>
      );

    }
    return <TestButtonButton button={record} />;

  };

  const ConfirmDialog = () => {

    const { getValues } = useFormContext();
    const [update] = useUpdate();
    if (!record) {

      return null;

    }

    const handleSave = (moveButtons: boolean) => {

      const { id, ...data } = getValues();

      const copiedData = { ...data } as Asset & { device: Device };

      if (copiedData.insideZones) {

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete copiedData.insideZones;

      }

      if (!moveButtons && copiedData.position) {

        copiedData.position.coordinates = record.position?.coordinates || copiedData.position.coordinates;

      }

      const onSuccess = () => {

        setConfirmDialogOpen(false);
        onDeviceSuccess();

      };

      const updateData = { name: copiedData.name, position: copiedData.position };

      update(
        'assets',
        { id, data: updateData, previousData: record },
        { onSuccess },
      );

    };

    return (
      <Dialog open={confirmDialogOpen} onClose={() => handleSave(false)}>
        <DialogTitle>{translate('resources.buttons.automove_title')}</DialogTitle>
        <DialogContent>
          <p>{translate('resource.buttons.automove_subtitle')}</p>
        </DialogContent>
        <DialogActions>
          <StyledButton
            label="resource.buttons.automove_no"
            onClick={() => {

              handleSave(false);

            }}
          />
          <StyledButton
            label="resource.buttons.automove_yes"
            onClick={() => {

              handleSave(true);

            }}
          />
        </DialogActions>
      </Dialog>
    );

  };

  return (
    <>
      <Card sx={{ boxShadow: 1 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Edit
            id={record?._id}
            resource="assets"
            actions={false}
            mutationMode="pessimistic"
            disableAuthentication
            mutationOptions={{ onSuccess: onDeviceSuccess }}
            redirect={false}
            transform={transform}>
            <EditFormWithSubmit setConfirmDialogOpen={setConfirmDialogOpen} confirmDialogOpen={confirmDialogOpen} onCollapse={props.onCollapse}>
              <ConfirmDialog/>
              <Stack spacing={1.5}>
                <Box className="flex-in-between">
                  <ExternalIdField/>
                  <TestButtonOrOrderExpl />
                </Box>
                <DeviceStatusWarning active={active} />
                <DeviceEditFields onCollapse={props.onCollapse} setConfirmDialogOpen={setConfirmDialogOpen}/>
              </Stack>
            </EditFormWithSubmit>
          </Edit>
        </CardContent>
      </Card>
    </>
  );

};

const DivWithId = ({ children, className }: PropsWithChildren & { className: string }) => {

  const record = useRecordContext();
  if (!record) {

    return null;

  }
  return (
    <div className={className} id={`device-${record.id}`}>{children}</div>
  );

};

const DeviceStatusIcon = (props: { createAlarmMode?: boolean; deviceStatus: Record<string, DeviceConnectionStatus> }) => {

  const record: Asset & { device: Device } | undefined = useRecordContext();
  const theme = useTheme();
  const translate = useTranslate();

  if (!record) {

    return null;

  }

  let active = props.deviceStatus?.[record._id] ? props.deviceStatus[record._id] : 'unknown';

  if (record.device.deviceId?.substring(0, 4) === 'Demo') {

    active = 'connected';

  }
  if (record.device.deviceId?.substring(0, 6) === 'Unpaid') {

    return (
      <ShoppingCartIcon sx={{
        marginLeft: '-2px !important',
        marginRight: '-3px !important',
      }}/>
    );

  }

  if (active === 'unknown') {

    return (
      <Tooltip title={translate('resources.buttons.text.status.pending')} placement="top" arrow>
        <CircularProgress size={15} sx={{
          width: '15px',
          verticalAlign: 'middle',
          marginRight: '0',
        }}/>
      </Tooltip>
    );

  }

  if (active === 'connected') {

    return (
      <Tooltip title={translate('resources.buttons.text.status.online')} placement="top" arrow>
        <CheckCircleIcon
          style={{
            width: '15px',
            verticalAlign: 'middle',
            position: 'relative',
            top: '-1px',
            marginRight: '0',
            color: theme.palette.primary.main,
          }} />
      </Tooltip>
    );

  }

  if (active === 'disconnected') {

    return (
      <Tooltip title={translate('resources.buttons.text.status.offline')} placement="top" arrow>
        <NotInterestedIcon
          style={{
            width: '15px',
            verticalAlign: 'middle',
            position: 'relative',
            top: '-1px',
            marginRight: '0',
            color: theme.palette.warning.main,
          }} />
      </Tooltip>
    );

  }

  if (active === 'empty') {

    return (
      <Tooltip title={translate('resources.buttons.text.status.empty')} placement="top" arrow>
        <BatteryAlertIcon
          style={{
            width: '17px',
            verticalAlign: 'middle',
            position: 'relative',
            top: '-1px',
            marginRight: '5px',
            color: theme.palette.warning.main,
          }} />
      </Tooltip>
    );

  }

  if (active === 'highNoise') {

    return (
      <Tooltip title={translate('resources.buttons.text.status.high_noise')} placement="top" arrow>
        <WifiTetheringErrorIcon
          style={{
            width: '17px',
            verticalAlign: 'middle',
            position: 'relative',
            top: '-1px',
            color: theme.palette.warning.main,
            marginLeft: '-1px',
            marginRight: '-1px',
          }} />
      </Tooltip>
    );

  }

  if (active === 'lowRSSI') {

    return (
      <Tooltip title={translate('resources.buttons.text.status.low_rssi')} placement="top" arrow>
        <SignalCellularConnectedNoInternet1BarIcon
          style={{
            width: '17px',
            verticalAlign: 'middle',
            position: 'relative',
            top: '-1px',
            color: theme.palette.warning.main,
            marginLeft: '-1px',
            marginRight: '-1px',
          }} />
      </Tooltip>
    );

  }

  return null;

};

const DeviceListName = () => {

  const record: Asset & { device: Device } | undefined = useRecordContext();

  if (!record) {

    return null;

  }
  return (
    <span style={{ fontWeight: 'bold', marginLeft: '15px' }} >{record.name}</span>
  );

};

const MassMoveList = (props: {
  selectedIds: Identifier[];
  setDialogOpen: any;
  setZoomed: any;
  setNeedsMonitoredZonesUpdate: React.Dispatch<React.SetStateAction<boolean>>;
}) => {

  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const [massPosition, setMassPosition] = useState<PointGeometry | null>(null);
  const [hasError, setHasError] = useState(false);

  const MoveMultipleButton = () => {

    const { selectedIds } = props;
    const handleMoveMultiple = () => {

      if (!massPosition) {

        notify('resources.notifications.move_multiple_no_location', { type: 'error' });
        return;

      }
      xgacDataProvider.massMoveDevices(selectedIds, massPosition).then(() => {

        notify('resources.notifications.moved_multiple', { type: 'success' });
        refresh();
        props.setDialogOpen(false);
        updateMonitoredZones();
        wait(500).then(() => {

          props.setZoomed(false);

        });

      }).catch(() => {

        notify('resources.notifications.moved_multiple_error', { type: 'error' });
        props.setDialogOpen(false);

      });

    };
    return (
      <BhvkDialogConfirmButton
        onClick={handleMoveMultiple}
        disabled={massPosition === null || selectedIds?.length === 0}
      >
        {translate('resources.buttons.text.move_confirm_button')}
      </BhvkDialogConfirmButton>
    );

  };

  return (
    <>
      <DialogContent>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>{translate('resources.buttons.text.mass_move_explanation')}</Typography>
        <InputLabel className="input-label">
          {translate('general.text.location')}
        </InputLabel>
        <GoogleCoordinatesInput
          className="bhvk-gray-input"
          value={massPosition}
          setValue={setMassPosition}
          country={['nl', 'be', 'de']}
          label={translate('resources.buttons.text.building_dialog.0.input_label')}
          setHasError={setHasError}
          sx={{
            mt: 0,
            '& .MuiOutlinedInput-root': {
              width: '100%',
              height: '40px',
            },
          }}
          inputProps={{
            endAdornment: <InputAdornment position="end"><SearchIcon /></InputAdornment>,
          }}
        />
        {hasError && (
          <Alert severity='warning' sx={{
            marginTop: '10px',
          }}>{translate('resources.buttons.text.invalid_address')}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ mt: 2 }}>
        <BhvkDialogCancelButton onClick={() => props.setDialogOpen(false)}>
          {translate('ra.action.cancel')}
        </BhvkDialogCancelButton>
        <MoveMultipleButton/>
      </DialogActions>
    </>
  );

};

export const ButtonList = (props?: { createAlarmMode?: boolean }) => {

  const { createAlarmMode } = props || { createAlarmMode: false };

  const notify = useNotify();
  const currentCustomer = getCurrentCustomer()?.value;
  const currentCustomerName = getCurrentCustomer()?.label;
  const [create, { error }] = useCreate();
  const translate = useTranslate();
  const [expandedId, setExpandedId] = useState<Identifier>('');
  const [, toggleExpanded] = useExpanded('all-assets', expandedId, true);
  const [map, setMap] = useState<Map | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [mapTileLayer, setMapTileLayer] = useState<string>(localStorage.getItem('mapTileLayer') || 'Default');
  const [createAlarmOpen, setCreateAlarmOpen] = useState<null | Asset & { device: Device; id: Identifier }>(null);
  const [movedLocked, setMovedLocked] = useState(true);
  const [simulateResponse, setSimulateResponse] = useState<null | JSX.Element[] | 'nobody' >(null);
  const isTrial = authProvider.getRoles().includes('bhvk_trial');
  const redirect = useRedirect();
  const [openStep, setOpenStep] = useState<null | number>(null);
  const [typeToAdd, setTypeToAdd] = useState<null | 'gateway' | 'button'>(null);
  const [upperSearchTerm, setUpperSearchTerm] = useState('');
  const [searchFocussed, setSearchFocussed] = useState(false);
  const [customerLinks, setCustomerLinks] = useState<CustomerLink[]>([]);
  const [possibleFloors, setPossibleFloors] = useState<number[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<DeviceFilter>({
    floors: [],
    deviceType: [],
    activeStatus: [],
  });
  const [selectedBuildings, setSelectedBuildings] = useState<AnyGeometry[] | null>(null);
  const [cancelLastClicked, setCancelLastClicked] = useState<Date | null>(null);
  const [lastSavedCustomer, setLastSavedCustomer] = useState<string | null>(null);
  const [fullCustomer, setFullCustomer] = useState<Customer | null>(null);
  const [deviceStatus, setDeviceStatus] = useState<Record<string, DeviceConnectionStatus>>({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [needsMonitoredZoneUpdate, setNeedsMonitoredZoneUpdate] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<{ value: string; label: string }[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  const identiy = useGetIdentity();

  const theme = useTheme();

  const { overlays } = useOverlay(map, selectedFilters.floors);

  const setChosenFloors = (floors: number[]) => {

    setSelectedFilters({ ...selectedFilters, floors });

  };

  useEffect(() => {

    toggleExpanded();

  }, [expandedId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    return () => {

      if (needsMonitoredZoneUpdate) {

        updateMonitoredZones();

      }

    };

  }, [needsMonitoredZoneUpdate, lastSavedCustomer]);

  useEffect(() => {

    const handleUnload = () => {

      if (needsMonitoredZoneUpdate) {

        updateMonitoredZones();

      }

    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {

      window.removeEventListener('beforeunload', handleUnload);

    };

  }, [needsMonitoredZoneUpdate]);

  useEffect(() => {

    if (localStorage.getItem('openBuildingDialog')) {

      setOpenStep(0);
      localStorage.removeItem('openBuildingDialog');

    }

    return () => {

      sessionStorage.removeItem('button_selectedIds');

    };

  }, []);

  useEffect(() => {

    if (currentCustomer && currentCustomer !== lastSavedCustomer) {

      setLastSavedCustomer(currentCustomer);
      setZoomed(false);
      sessionStorage.removeItem('button_selectedIds');

    }

  }, [currentCustomer, lastSavedCustomer, identiy]);

  useEffect(() => {

    const getFullCustomer = async () => {

      if (!lastSavedCustomer) {

        return;

      }
      const fullCustomerRequest = await xgacDataProvider.getOne('customers', {
        id: lastSavedCustomer,
      });
      setFullCustomer(fullCustomerRequest.data as Customer & { id: Identifier });

    };
    getFullCustomer();

  }, [lastSavedCustomer]);

  const listResult = useGetList('specials/bhv-knop/device-list', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {
      $search: upperSearchTerm !== '' ? upperSearchTerm : undefined,
      ...(createAlarmMode ? { type: '!loraGateway' } : {}),
    },
  });
  const { total, isLoading } = listResult;
  const data = listResult.data?.filter((item: Asset & { device: Device }) => {

    let canPass = true;
    if (selectedFilters.floors.length > 0) {

      if (!selectedFilters.floors.includes(item.position?.properties?.address?.floor || 0)) {

        canPass = false;

      }

    }
    if (canPass && selectedFilters.deviceType.length > 0) {

      if (!selectedFilters.deviceType.includes(item.device.type)) {

        canPass = false;

      }

    }

    if (selectedGroups.length > 0) {

      if (!item.assetGroups.some((group) => selectedGroups.includes(group._id))) {

        canPass = false;

      }

    }

    if (canPass && selectedFilters.activeStatus.length > 0) {

      let passesActive = false;
      if (selectedFilters.activeStatus.includes(DeviceStatus.Demo)) {

        if (item.device.deviceId.substring(0, 4) === 'Demo') {

          passesActive = true;

        }

      }

      if (!passesActive && selectedFilters.activeStatus.includes(DeviceStatus.Unpaid)) {

        if (item.device.deviceId.substring(0, 6) === 'Unpaid') {

          passesActive = true;

        }

      }
      if (!passesActive && selectedFilters.activeStatus.includes(DeviceStatus.Active)) {

        if (item.device.deviceId.substring(0, 6) !== 'Unpaid' && item.device.deviceId.substring(0, 4) !== 'Demo') {

          passesActive = true;

        }

      }
      if (!passesActive) {

        canPass = false;

      }

    }
    return canPass;

  }) as (Asset & { device: Device })[];

  useEffect(() => {

    if (!lastSavedCustomer) {

      return;

    }
    const getProcessTemplates = async () => {

      const customerLinkRequest = await atsDataProvider.getList('customer-links', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'createdAt', order: 'DESC' },
        filter: {},
      });

      if (!customerLinkRequest.data) {

        setCustomerLinks([]);

      }
      setCustomerLinks(customerLinkRequest.data);

    };

    getProcessTemplates();

  }, [lastSavedCustomer]);

  useEffect(() => {

    if (createAlarmMode && createAlarmOpen !== null) {

      const currentRecord = data.find((item) => item._id === createAlarmOpen._id);
      if (!currentRecord) {

        return;

      }
      const mappedAssetGroups = currentRecord.assetGroups.map((item) => item._id || item);
      const filteredCustomerLinks = customerLinks.filter((item: CustomerLink) => {

        return _.intersection(item.xgacGroups, [...mappedAssetGroups, -2]).length > 0
          && _.intersection(item.notXgacGroups, mappedAssetGroups).length === 0
          && item.alarmLevels.red;

      });
      const simulationIds = filteredCustomerLinks.map((currentLink) => currentLink.processTemplate?.id) || ['334df770-17c7-4dbb-8f6b-eebb5f75e270'];

      setSimulateResponse(null);
      const simulatePromises = [];
      const simulateResponses: JSX.Element[] = [];
      for (let i = 0; i < simulationIds.length; i++) {

        const simulationId = simulationIds[i];
        const customerLink = filteredCustomerLinks[i];
        simulatePromises.push(atsDataProvider.simulate(simulationId, createAlarmOpen.id.toString()).then((response: any) => {

          if (!response.json || Object.keys(response.json).length === 0) {

            setSimulateResponse('nobody');
            return;

          }
          let totalResponders = 0;
          for (const key of Object.keys(response.json)) {

            totalResponders += response.json[key].filter((item: any) => item.name || item.alarmCenter).length;

          }
          if (totalResponders === 0) {

            setSimulateResponse('nobody');
            return;

          }
          simulateResponses.push(<div key={customerLink.id}>
            {Object.keys(response.json).map((key, index) => {

              const hasResults = response.json[Object.keys(response.json)[index - 1]]?.length > 0;
              const hasPersons = response.json[key].some((item: any) => item.name);
              const taskContactedFilter = customerLink?.processTemplate?.scheduleTasks[index]?.metaScript?.meta?.xgacFilter?.result?.contacted
                || customerLink?.processTemplate?.scheduleTasks[index]?.metaScript?.meta?.xgacFilter?.request?.contacted;
              const hasContactedFilter = taskContactedFilter === '!=true';
              const delay = customerLink?.processTemplate?.scheduleTasks[index]?.delay || 0;
              const isAlarmCenterStep = response.json[key].some((item: any) => item.alarmCenter);

              if (!isAlarmCenterStep && !hasPersons) {

                return null;

              }

              return (
                <>
                  <div className={'sf-arrow'}>
                    {hasResults === false
                      ? <img src={arrowDownIcon} alt="arrow down"/>
                      : <div>
                        <img src={arrowDownIcon} alt="arrow down"/>
                        {delay / 1000} {translate('general.text.seconds')}
                      </div>
                    }
                  </div>
                  <div className={`sf-step ${hasPersons ? 'sf-has-persons' : 'sf-no-persons'}`}>
                    {hasPersons === false
                      ? <h6>{customerLink.processTemplate.scheduleTasks[index]?.taskTemplate?.templateName || `fase ${index + 1}`}</h6>
                      : <>
                        <h5>BHV-oproep</h5>
                        <small>({response.json[key].length} {translate('general.text.person', { smart_count: response.json[key].length })})</small>
                        <ul>
                          {hasContactedFilter && index !== 0
                            ? (<>
                              <li>
                                <PersonIcon style={{
                                  verticalAlign: 'middle', position: 'relative', top: '-2px', marginRight: '1px',
                                }}/>
                                {translate('resources.buttons.text.create_alarm_uncontacted', { smart_count: response.json[key].length })}
                              </li>
                            </>
                            ) : (
                              <>
                                {response.json[key].map((item: any) => (
                                  <>
                                    <li>
                                      <PersonIcon style={{
                                        verticalAlign: 'middle',
                                        position: 'relative',
                                        top: '-2px',
                                      }}/> {item.name}
                                    </li>
                                  </>
                                ))}
                              </>
                            )}
                        </ul>
                      </>
                    }
                  </div>
                </>
              );

            })}
          </div>);

        }));

      }

      Promise.all(simulatePromises).then(() => {

        if (simulateResponses.length === 0) {

          setSimulateResponse('nobody');

        }

        setSimulateResponse(simulateResponses.sort((a, b) => {

          if (a.key && b.key) {

            return a.key.localeCompare(b.key);

          }
          return 0;

        }));

      });

    }

  }, [createAlarmMode, createAlarmOpen, customerLinks]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    if (data && data.length > 0 && map && !zoomed) {

      const allCoordinates = data.filter((item) => {

        return !!item.position?.coordinates;

      }).map((item: any) => [item.position.coordinates[1], item.position.coordinates[0]]);
      map.fitBounds(allCoordinates as LatLngBoundsExpression);
      setZoomed(true);

    }

  }, [data, map, zoomed]);

  useEffect(() => {

    if (!listResult.data) return;

    const newStatus: Record<string, DeviceConnectionStatus> = {};
    listResult.data.forEach((item: Asset & { device: Device }) => {

      newStatus[item._id] = getDeviceStatus(item);

    });

    setDeviceStatus(newStatus);
    const assetGroupsSet = new Set<string>();
    listResult.data.forEach((item: Asset & { device: Device }) => {

      item.assetGroups.forEach((group) => {

        const groupId = typeof group === 'string' ? group : group._id;
        assetGroupsSet.add(groupId);

      });

    });

    xgacDataProvider.getList('asset-groups', {
      pagination: { page: 1, perPage: assetGroupsSet.size },
      sort: { field: 'name', order: 'ASC' },
      filter: { _id: Array.from(assetGroupsSet) },
    }).then((groupListResult) => {

      setAvailableGroups(groupListResult.data.map((group) => ({
        value: group._id || '',
        label: group.name || '',
      })));

    });

  }, [listResult.data]);

  useEffect(() => {

    if (data && data[0]?.customer?._id !== currentCustomer && zoomed) {

      setZoomed(false);

    }

  }, [currentCustomer, data, zoomed]);

  useEffect(() => {

    if (!listResult.data) return;
    const floors = listResult.data.map((item: Asset & { device: Device }) => {

      return item.position?.properties?.address?.floor || 0;

    });
    const combinedFloors = [...floors, ...overlays.map((item) => item.level || 0)];
    const uniqueFloors = _.uniq(combinedFloors).sort((a, b) => a - b);

    if (!_.isEqual(uniqueFloors, possibleFloors)) {

      setPossibleFloors(uniqueFloors);
      setChosenFloors([]);

    }

  }, [listResult.data, overlays]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    if (selectedBuildings) {

      map?.fitBounds(L.geoJSON(selectedBuildings).getBounds(), { maxZoom: 19 });
      setSelectedBuildings(null);

    }

  }, [selectedBuildings]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleItemExpansion = async (id: Identifier) => {

    if (data) {

      const currentItem = data?.find((item: any) => item._id === id);
      if (currentItem && currentItem.position?.coordinates) {

        const coordinates = [currentItem.position.coordinates[1], currentItem.position.coordinates[0]];
        if (coordinates && !map?.getBounds().contains(coordinates as LatLngExpression)) {

          map?.flyTo(coordinates as LatLngExpression);

        }

      }

    }

    if (expandedId === id) {

      setExpandedId('');
      return false;

    }
    setExpandedId(id);
    await wait(1);
    document.getElementById(`device-${id}`)?.scrollIntoView({ block: 'start' });
    return false;

  };

  const ExternalIdField = (innerProps: { externalId?: string }) => {

    if (!innerProps.externalId) return (<></>);
    return (
      <div className="button-sidebar-grid-external_id">
        <span>{innerProps.externalId}</span>
      </div>
    );

  };

  const ButtonSideBar = () => {

    const unselectAll = useUnselectAll('specials/bhv-knop/device-list');
    const [searchTerm, setSearchTerm] = useState(upperSearchTerm);
    const [mouseHoverId, setMouseHoverId] = useState<Identifier | null>(null);
    const [selectedIds, setSelectedIds] = useState<Identifier[]>(
      sessionStorage.getItem('button_selectedIds') ? JSON.parse(sessionStorage.getItem('button_selectedIds') as string) : [],
    );
    const showBulkActions = selectedIds.length > 0;
    const [massMoveOpen, setMassMoveOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const refresh = useRefresh();

    useEffect(() => {

      sessionStorage.setItem('button_selectedIds', JSON.stringify(selectedIds));

    }, [selectedIds]);

    useEffect(() => {

      const clearSelection = () => {

        setSelectedIds([]);
        sessionStorage.removeItem('button_selectedIds');

      };
      window.addEventListener(CURRENT_CUSTOMER_CHANGED_EVENT, clearSelection);
      return () => window.removeEventListener(CURRENT_CUSTOMER_CHANGED_EVENT, clearSelection);

    }, []);

    useEffect(() => {

      if (mouseHoverId) {

        const type = data?.find((item) => item._id === mouseHoverId)?.device.type;

        if (type === 'loraGateway') {

          document.getElementById(`marker-${mouseHoverId}`)?.classList.add('leaflet-gateway-hover');

        } else {

          document.getElementById(`marker-${mouseHoverId}`)?.classList.add('leaflet-marker-hover');

        }

      }
      return () => {

        const type = data?.find((item) => item._id === mouseHoverId)?.device.type;
        if (type === 'loraGateway') {

          document.getElementById(`marker-${mouseHoverId}`)?.classList.remove('leaflet-gateway-hover');

        } else {

          document.getElementById(`marker-${mouseHoverId}`)?.classList.remove('leaflet-marker-hover');

        }

      };

    }, [mouseHoverId]);

    useEffect(() => {

      const debouncedSearch = setTimeout(() => {

        setUpperSearchTerm(searchTerm);

      }, 500);
      return () => {

        clearTimeout(debouncedSearch);

      };

    }, [searchTerm]);

    useEffect(() => {

      unselectAll();

    }, [unselectAll]);

    const resetSearch = () => {

      setSearchTerm('');
      setUpperSearchTerm('');
      setZoomed(false);

    };

    const printLabels = () => {

      const regex = /^[a-z0-9]+$/i;
      if (!data) return;
      const labels = data.filter((item: Asset & { device: Device }) => {

        const isSelected = showBulkActions ? selectedIds.includes(item._id) : true;
        return isSelected && regex.test(item.device.deviceId);

      }).map((item: Asset & { device: Device }) => ({
        devEui: item.device.deviceId,
        sequenceNumber: item.externalId?.split('-')?.slice(-1)[0] || '',
      }));

      if (labels.length === 0) {

        notify('resources.buttons.text.no_labels_selected', { type: 'warning' });
        return;

      }

      window.open(
        buildLabelPrintUrl(labels, identiy.data?.fullName || '', currentCustomerName || ''),
        '_blank',
        'noopener',
      );

    };

    const handleGoToOrder = () => {

      const conceptButtons: Identifier[] = [];
      const conceptGateways: Identifier[] = [];
      data?.filter((item: Asset & { device: Device }) => {

        const hasSubstring = item.device.deviceId?.substring(0, 6) === 'Unpaid';
        if (showBulkActions) {

          return selectedIds.includes(item._id) && hasSubstring;

        }
        return hasSubstring;

      }).forEach((item: Asset & { device: Device }) => {

        if (item.device.type === DeviceType.Lora) {

          conceptButtons.push(item._id);

        } else if (item.device.type === DeviceType.LoraGateway) {

          conceptGateways.push(item._id);

        }

      });
      if (conceptButtons.length > 0 || conceptGateways.length > 0) {

        sessionStorage.setItem('conceptDevices', JSON.stringify({ conceptButtons, conceptGateways }));
        redirect('/bestellen');

      } else {

        notify('resources.buttons.text.no_concept_devices');

      }

    };

    const handleDeleteDevices = async () => {

      const assetsToDelete = listResult.data?.filter((item: Asset & { device: Device }) => {

        const hasSubstring = item.device.deviceId?.substring(0, 6) === 'Unpaid' || item.device.deviceId?.substring(0, 4) === 'Demo';
        if (showBulkActions) {

          return selectedIds.includes(item._id) && hasSubstring;

        }
        return hasSubstring;

      }).map((item: Asset & { device: Device }) => {

        return {
          _id: item._id,
          _ref: 'Asset',
        };

      });
      if (assetsToDelete?.map((item) => item._id).includes(expandedId.toString())) {

        setExpandedId('');

      }

      if (assetsToDelete) {

        try {

          const deleteDevicesResponse = await httpClient(
            `${apiUrl}/specials/bhv-knop/remove-unpaid-devices`,
            { method: 'POST' },
            {
              assets: assetsToDelete,
            },
          );
          if (deleteDevicesResponse) {

            notify('resources.buttons.text.deleted_unpaid_devices', { type: 'success' });
            refresh();

          }
          sessionStorage.setItem('button_selectedIds', JSON.stringify([]));
          setSelectedIds([]);

        } catch (e) {

          notify('resources.buttons.text.deleted_unpaid_devices_error', { type: 'error' });

        }

      }

    };

    const ButtonDataGridListItem = () => {

      const record = useRecordContext();
      if (!record) return <></>;
      const id = record._id;

      const checkResponseButtonSx = {
        display: 'block',
        minHeight: 'fit-content',
        minWidth: 'fit-content',
      } as const;

      return (
        <div
          onMouseEnter={() => setMouseHoverId(id)}
          onMouseLeave={() => setMouseHoverId(null)}
          style={{ cursor: 'pointer' }}
        >
          <DivWithId className={selectedIds.includes(id) ? 'button-sidebar-grid-row selected-row' : 'button-sidebar-grid-row'} >
            {(mouseHoverId !== id && !showBulkActions) ? (
              <>
                <div className="button-sidebar-grid-name">
                  <DeviceStatusIcon deviceStatus={deviceStatus} createAlarmMode={createAlarmMode}/>
                  <DeviceListName/>
                </div>
                {createAlarmMode
                  ? <StyledButton label="menu.items.response" onClick={resetSearch} sx={checkResponseButtonSx}/>
                  : <ExternalIdField externalId={record.externalId?.split('-')?.slice(-1)[0]} />}
              </>
            ) : (
              <>
                <div className="button-sidebar-grid-name">
                  <Checkbox
                    checked={selectedIds.includes(id)}
                    size="small"
                    className="button-sidebar-checkbox"
                    onClick={(e) => {

                      e.stopPropagation();
                      if (selectedIds.includes(id)) {

                        setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));

                      } else {

                        setSelectedIds([...selectedIds, id]);

                      }

                    }}/>
                  <DeviceListName/>
                </div>
                {/* eslint-disable-next-line no-nested-ternary */}
                {createAlarmMode
                  ? <StyledButton label="menu.items.response" onClick={resetSearch} sx={checkResponseButtonSx}/>
                  : mouseHoverId === id ? <IconButton size="small" sx={{ padding: 0, margin: 0 }}>
                    <EditIcon spacing={0} sx={{ padding: 0, margin: 0, fontSize: '20px' }}/>
                  </IconButton> : <ExternalIdField externalId={record.externalId?.split('-')?.slice(-1)[0]} />
                }
              </>
            )}
          </DivWithId>
        </div>
      );

    };

    const UnpaidBanner = () => {

      const hasUnpaid = data?.some((item: Asset & { device: Device }) => {

        return item.device.deviceId?.substring(0, 6) === 'Unpaid';

      });
      if (!hasUnpaid) return <></>;
      return <Grid className="button-sidebar-unpaid-banner" container>
        <Grid xs={8}>
          <div>
            <h4>{translate('resources.buttons.text.unpaid_devices')}</h4>
            <span onClick={() => {

              const newSelectedIds = listResult.data?.filter((item: Asset & { device: Device }) => {

                if (selectedIds.includes(item._id)) {

                  return item.device.deviceId?.substring(0, 6) !== 'Unpaid';

                }
                return false;

              }).map((item: Asset & { device: Device }) => item._id) || [];
              setSelectedIds(newSelectedIds);
              setDeleteDialogOpen(true);

            }
            }>{translate('resources.buttons.text.delete_unpaid')}</span>
          </div>
        </Grid>
        <Grid xs={4} style={{
          display: 'flex',
          justifyContent: 'end',
          alignItems: 'flex-end',
        }}>
          <button onClick={handleGoToOrder}>
            <ShoppingCartIcon/>
            <span>{translate('general.text.order_now')}</span>
          </button>
        </Grid>
      </Grid>;

    };

    return (
      <>
        <div className="button-sidebar-div">
          <Dialog open={massMoveOpen} sx={{ zIndex: '7' }} maxWidth="md" fullWidth>
            <DialogTitle sx={{ paddingBottom: 0 }}><span>{translate('resources.buttons.text.mass_move_title')}</span></DialogTitle>
            <MassMoveList
              selectedIds={selectedIds}
              setDialogOpen={setMassMoveOpen}
              setZoomed={setZoomed}
              setNeedsMonitoredZonesUpdate={setNeedsMonitoredZoneUpdate}
            />
          </Dialog>
          {!createAlarmMode ? (
            <div className="button-sidebar-add">
              <h4>
                {translate(!showBulkActions ? 'ra.action.create' : 'resources.buttons.text.actions_with_selected')}
                <div>
                  {authProvider.isAdmin() && (
                    <StyledButton
                      label={showBulkActions ? 'resources.buttons.text.print_selected_labels' : 'resources.buttons.text.print_all_labels'}
                      style={{ marginTop: '1em', marginRight: '10px' }}
                      color="primary"
                      onClick={printLabels}
                    />
                  )}
                </div>
              </h4>

              <Grid container spacing={1}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    aria-hidden={showBulkActions}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      visibility: showBulkActions ? 'hidden' : 'visible',
                      pointerEvents: showBulkActions ? 'none' : 'auto',
                    }}
                  >
                    <Grid container spacing={1}>
                      {typeToAdd === null ? (
                        <>
                          <Grid xs={4}>
                            <ButtonWithIconAndLabel
                              icon={buildingIcon}
                              label={translate('resources.buttons.text.building_dialog.building')}
                              onClick={() => setOpenStep(0)}
                            />
                          </Grid>
                          <Grid xs={4}>
                            <ButtonWithIconAndLabel
                              icon={bhvIcon}
                              label={translate('general.text.emergency_button')}
                              onClick={() => setTypeToAdd('button')}
                            />
                          </Grid>
                          <Grid xs={4}>
                            <ButtonWithIconAndLabel
                              icon={gatewayIcon}
                              label={translate('general.text.gateway')}
                              onClick={() => setTypeToAdd('gateway')}
                            />
                          </Grid>
                        </>
                      ) : (
                        <Grid xs={12}>
                          <ButtonPageButtons
                            icon={<CancelIcon />}
                            label="resources.buttons.text.stop_placing"
                            onClick={() => {

                              setCancelLastClicked(new Date());
                              setTypeToAdd(null);

                            }}
                          />
                        </Grid>
                      )}
                      <Grid xs={8}>
                        <MuiTextField
                          className="sidebar-search-input"
                          label=""
                          placeholder={translate('resources.buttons.text.search')}
                          fullWidth
                          color="info"
                          variant="outlined"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onFocus={() => setSearchFocussed(true)}
                          onBlur={() => setSearchFocussed(false)}
                          autoFocus={searchFocussed}
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position={'start'}>
                                <SearchIcon/>
                              </InputAdornment>
                            ),
                            endAdornment: (
                              searchTerm.length > 0 && (
                                <InputAdornment position={'end'}>
                                  <IconButton onClick={resetSearch} size="small">
                                    <CloseIcon fontSize="small"/>
                                  </IconButton>
                                </InputAdornment>
                              )
                            ),
                          }}
                        />
                      </Grid>
                      <Grid xs={4}>
                        <button
                          className={(selectedFilters.floors.length > 0 || selectedFilters.activeStatus.length > 0 || selectedFilters.deviceType.length > 0)
                            ? 'filter-device-button filter-device-button-active'
                            : 'filter-device-button'
                          }
                          onClick={() => {

                            setFilterOpen(!filterOpen);

                          }}
                        >
                          <TuneIcon/>
                          <span>
                            {translate((selectedFilters.floors.length > 0 || selectedFilters.activeStatus.length > 0 || selectedFilters.deviceType.length > 0)
                              ? 'general.text.filtered'
                              : 'general.text.filter')}
                          </span>
                        </button>
                      </Grid>
                    </Grid>
                    <DeviceFilters
                      filters={selectedFilters}
                      onChange={setSelectedFilters}
                      possibleFloors={possibleFloors}
                      open={filterOpen}
                      setOpen={setFilterOpen}
                    />
                  </Box>
                  <Box
                    aria-hidden={!showBulkActions}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      justifyContent: 'center',
                      visibility: showBulkActions ? 'visible' : 'hidden',
                      pointerEvents: showBulkActions ? 'auto' : 'none',
                    }}
                  >
                    <Grid
                      container
                      spacing={1}
                    >
                      <Grid xs={3} sx={{ display: 'flex' }}>
                        <ButtonPageButtons
                          className="bulk-action-button"
                          icon={<RoomIcon />}
                          label="resources.buttons.text.mass_move"
                          onClick={() => setMassMoveOpen(true)}
                        />
                      </Grid>
                      <Grid xs={3} sx={{ display: 'flex' }}>
                        <ButtonPageButtons
                          className="bulk-action-button"
                          icon={<ShoppingCartIcon />}
                          label="general.text.order"
                          onClick={handleGoToOrder}
                        />
                      </Grid>
                      <Grid xs={3} sx={{ display: 'flex' }}>
                        <ButtonPageButtons
                          className="bulk-action-button"
                          icon={<IosShareIcon sx={{ transform: 'rotate(90deg)' }} />}
                          label="ra.action.export"
                          onClick={() => {

                            if (!data) return;

                            const selectedAssets = data?.filter(
                              (item: Asset & { device: Device }) => selectedIds.includes(item._id),
                            ) as Array<Asset & { device: Device }> || [];
                            buttonAssetExporter(selectedAssets);

                          }}
                        />
                      </Grid>
                      <Grid xs={3} sx={{ display: 'flex' }}>
                        <ButtonPageButtons
                          className="bulk-action-button"
                          icon={<DeleteIcon />}
                          label="ra.action.delete"
                          onClick={() => setDeleteDialogOpen(true)}
                          disabled={
                            !data?.some((item: Asset & { device: Device }) => (
                              selectedIds.includes(item._id)
                              && (item.device.deviceId?.substring(0, 6) === 'Unpaid'
                                || item.device.deviceId?.substring(0, 4) === 'Demo')
                            ))
                          }
                        />
                      </Grid>
                    </Grid>
                    {selectedIds.length === data?.length && (data?.length ?? 0) > 0 ? (
                      <button
                        type="button"
                        className="select-all-button deselect-all-button"
                        onClick={() => setSelectedIds([])}
                      >
                        <span>{translate('general.text.deselect_all')}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="select-all-button"
                        onClick={() => setSelectedIds(data?.map((item) => item._id) || [])}
                      >
                        <span>{translate('general.text.select_all')}</span>
                      </button>
                    )}
                  </Box>
                </Box>
              </Grid>
            </div>

          ) : (
            <div className="button-sidebar-header">
              <h4>{translate(createAlarmMode ? 'resources.alarms.text.create_an_alarm' : 'resources.buttons.text.manage_buttons')}</h4>
              <StyledButton label="resources.buttons.text.show_all" style={{ marginTop: '1em', marginRight: '10px' }} onClick={resetSearch}/>
              <StyledButton
                label={isTrial ? 'general.text.order' : 'general.text.order_extra'}
                onClick={() => redirect('/bestellen')}
                style={{ marginTop: '1em', marginRight: '10px' }}
                color="primary"
              />
            </div>
          )}
          <div className="button-sidebar-grid">
            <Datagrid
              resource="all-assets"
              data={data}
              empty={<></>}
              isLoading={isLoading}
              total={total}
              expandSingle={true}
              rowStyle={(record: any, index: number) => {

                if (selectedIds.includes(record._id)) {

                  return {
                    backgroundColor: '#9FD2B6',
                  };

                }
                return {
                  backgroundColor: index % 2 === 0 ? '#F0F9F5' : '#E2F1EA',
                };

              }}
              sx={{
                '& .RaDatagrid-expandedPanel': {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  backgroundColor: (data || []).indexOf((data || []).find((item: any) => item._id === expandedId)) % 2 === 0 ? '#F0F9F5' : '#E2F1EA',
                },
              }}
              bulkActionButtons={false}
              expand={createAlarmMode ? undefined : (
                <DeviceEditForm
                  setNeedsMonitoredZonesUpdate={setNeedsMonitoredZoneUpdate}
                  onCollapse={() => setExpandedId('')}
                />
              )}
              header={ <EmptyHeader/>}
              rowClick={createAlarmMode ? (id, resource, record) => {

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                setCreateAlarmOpen(record as Asset & { device: Device; id: Identifier });
                return false;

              } : 'toggleSelection'}
              onToggleItem={(id) => toggleItemExpansion(id)}
            >
              <ButtonDataGridListItem/>
            </Datagrid>
          </div>
          <UnpaidBanner/>
        </div>
        <Confirm
          content="resources.buttons.text.delete_add_again"
          isOpen={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteDevices}
          title="resources.buttons.text.delete_are_you_sure"
        />
      </>
    );

  };

  const createAlarm = async () => {

    const alarmSeverity = () => {

      if (createAlarmOpen && createAlarmOpen.device.loraConfig) {

        const mileSightConfig: MilesightConfig | undefined = createAlarmOpen.device.loraConfig.type === LoraDeviceTypes.Milesight
          ? createAlarmOpen.device.loraConfig?.config
          : undefined;
        if (mileSightConfig?.alarmSeverity && alarmCodeToColorMapping[mileSightConfig?.alarmSeverity as keyof typeof alarmCodeToColorMapping]) {

          return alarmCodeToColorMapping[mileSightConfig?.alarmSeverity as keyof typeof alarmCodeToColorMapping];

        }

      }
      if (fullCustomer && fullCustomer.loraConfig) {

        const mileSightConfig: MilesightConfig | undefined = fullCustomer.loraConfig.find(
          (config: AnyLoraConfig) => config.type === LoraDeviceTypes.Milesight,
        )?.config as MilesightConfig;
        if (mileSightConfig?.alarmSeverity && alarmCodeToColorMapping[mileSightConfig?.alarmSeverity as keyof typeof alarmCodeToColorMapping]) {

          return alarmCodeToColorMapping[mileSightConfig?.alarmSeverity as keyof typeof alarmCodeToColorMapping];

        }

      }

      return 'orange';

    };
    const creationData = {
      name: 'BHV-Oproep',
      type: 'assistance',
      severity: alarmSeverity(),
      asset: {
        _id: createAlarmOpen?._id,
        _ref: 'Asset',
      },
      position: createAlarmOpen?.position || null,
      procedure: 'BHV-knop demo alarm, geen actie nodig',
    };

    await create('alarms', {
      data: creationData,
    });

    if (!error) {

      notify('BHV-oproep aangemaakt, de BHV\'ers worden gealarmeerd.');
      redirect('/alarms?page=1');

    }

  };

  return <>
    <Confirm
      isOpen={createAlarmOpen !== null}
      title={createAlarmOpen?.name || ''}
      confirm={translate('menu.items.create_alarm')}
      confirmColor={'warning'}
      maxWidth={Array.isArray(simulateResponse) && simulateResponse.length > 1 ? 'lg' : 'sm'}
      fullWidth
      content={<div>
        {translate('resources.buttons.text.create_alarm_confirm_title')}
        <hr />
        <strong>{translate('resources.buttons.text.create_alarm_current_address')}</strong>
        {createAlarmOpen?.position?.properties?.address?.formattedAddress}
        <hr/>
        <div className={'simulationFlow'}>
          <div className="sf-alarm">{translate('general.text.alarm')}</div>
          {simulateResponse ? <>
            {(simulateResponse !== 'nobody' && simulateResponse.length > 0)
              ? <>
                <Grid container spacing={2}>
                  {simulateResponse.map((item: any) => (
                    <Grid xs={Math.max(Math.ceil(12 / simulateResponse.length), 3)}>
                      <Card>
                        {item}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
              : <div className="sf-step" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
                <Button
                  onClick={() => redirect('/help/dashboard/waarom-wordt-er-niemand-opgeroepen')}
                  size="small"
                  style={{ textTransform: 'none' }}
                  variant={'outlined'}
                  color={'primary'}
                  label={translate('resources.buttons.text.status.nobody_why')}
                />
              </div>
            }
          </>
            : (<div style={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress/>
            </div>)
          }
        </div>
      </div>}
      onConfirm={() => {

        createAlarm();
        setCreateAlarmOpen(null);

      }}
      onClose={() => setCreateAlarmOpen(null) }
    />
    <Grid container sx={{ height: '100%' }}>
      <Grid md={9} xs={9}>
        <MapContainer
          ref={(ref: any) => setMap(ref)}
          center={[52.1009166, 5.6462914]}
          zoom={17}
          id="button-map-container"
          minZoom={4}
          zoomControl={false}
          style={{ height: '100%' }}
        >
          <div>
            <div className="leaflet-control-container-custom">
              <CustomLayerControl
                map={map}
                toggleVisible={true}
                attribution={translate('resources.buttons.text.gateway_map_disclaimer')}
                setUpperTileLayer={setMapTileLayer}
              />
              <CenterControl onClick={() => {

                setZoomed(false);

              }}/>
              <button onClick={() => setMovedLocked(!movedLocked)} className="map-control-button" style={movedLocked ? {
                backgroundColor: theme.palette.error.main,
                color: 'white',
              } : {
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}>
                {movedLocked ? <LockIcon/> : <LockOpenIcon/>}
                <span>{translate(movedLocked ? 'resources.buttons.text.locked' : 'resources.buttons.text.unlocked')}</span>
              </button>
            </div>
            <div className="topleft-filter-div">
              <ListFilter
                items={availableGroups}
                selectedItems={selectedGroups}
                onChange={setSelectedGroups}
                label={'menu.items.groups'}
                labelSelected={'resources.buttons.groups_selected'}
              />
            </div>
            <div className="second-filter-div" style={{
              left: '260px',
            }}>
              <FloorFilter floors={possibleFloors} selectedFloors={selectedFilters.floors} onChange={setChosenFloors}/>
            </div>
          </div>
          <ScaleControl position="bottomright" imperial={false}/>
          {data && data.map((item: Asset & { device: Device }) => {

            if (item.position && item.position.coordinates && item.position.coordinates.length > 0) {

              return (<ButtonMapMarker
                createAlarmMode={createAlarmMode}
                setCreateAlarmOpen={setCreateAlarmOpen}
                key={item._id}
                item={item}
                moveLocked={movedLocked}
                expandedId={expandedId}
                setExpandedId={setExpandedId}
                toggleItemExpansion={toggleItemExpansion}
                mapTileLayer={mapTileLayer}
                setNeedsMonitoredZonesUpdate={setNeedsMonitoredZoneUpdate}
              />);

            }
            return null;

          })}
        </MapContainer>
      </Grid>
      <Grid md={3} xs={3}>
        <ButtonSideBar />
      </Grid>
    </Grid>
    <AddBuildingDialog
      openStep={openStep}
      setOpenStep={setOpenStep}
      setChosenFloors={setChosenFloors}
      setChosenBuilding={setSelectedBuildings}
      data={data}
    />
    <AddButtonOrGatewayDialog
      setAddType={setTypeToAdd}
      setChosenFloors={setChosenFloors}
      addType={typeToAdd}
      map={map}
      cancelLastClicked={cancelLastClicked}
      setSelectedBuilding={setSelectedBuildings}
      chosenFloors={selectedFilters.floors}
    />
  </>;

};
